import { ImagePlus, Minus, Plus, Target, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { apiClient, type SocialReportDashboard, type ThemeNote } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ChartDownload } from "@/shared/components/charts/chart-kit";
import { Panel } from "@/shared/components/charts/Panel";
import { RankedBars } from "@/shared/components/charts/RankedBars";
import { MultiSelectFilter, type MultiSelectOption } from "@/shared/components/MultiSelectFilter";
import { metricLabel, sourceOptions, type DashboardDimension, type DashboardFacets } from "@/shared/filters/snapshot-facets";
import { formatCompact, formatDateTime, formatInteger } from "@/shared/utils/format";

import { draftThemeText, offersSourceChoice, recorteKey, themeKpis, themeRecorte } from "./dashboard-v1";
import type { ReportReference } from "./StrategicMatrixPanel";
import { useUnsavedChanges } from "@/shared/unsaved-changes/UnsavedChanges";

export type FacetControls = {
  toggle: (key: DashboardDimension, value: string) => void;
  setExclude: (key: DashboardDimension, exclude: boolean) => void;
  clear: (key: DashboardDimension) => void;
  options: (key: DashboardDimension) => Array<MultiSelectOption<string>>;
};

/** O cabeçalho do v1: o tema, quando é um só; senão "Análise temática",
 *  com os grupos e temas escolhidos embaixo. */
export function themeHeading(facets: DashboardFacets, data: SocialReportDashboard): { title: string; groups: string; themes: string } {
  const groups = [...facets.themeGroups.values];
  const themes = [...facets.themes.values];
  const excluded = (exclude: boolean) => (exclude ? "Exceto " : "");
  const groupsText = !groups.length ? "" : groups.length <= 2 ? `${excluded(facets.themeGroups.exclude)}${groups.join(" & ")}` : `${groups.length} temas selecionados`;
  let themesText: string;
  if (themes.length === 0) {
    const inPhoto = data.themes.filter(item => item.key !== "__none__").map(item => item.key);
    themesText = groups.length ? inPhoto.slice(0, 15).join(" | ") + (inPhoto.length > 15 ? "…" : "") : "Todos os subtemas do recorte";
  } else {
    themesText = themes.length <= 10 ? `${excluded(facets.themes.exclude)}${themes.join(" | ")}` : `${themes.length} subtemas selecionados`;
  }
  const title = themes.length === 1 && !facets.themes.exclude ? themes[0] : "Análise temática";
  return { title, groups: groupsText, themes: themesText };
}

type Avatar = { kind: "initials" | "emoji"; text: string } | { kind: "image"; src: string };

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]?.toLocaleUpperCase("pt-BR")).join("") || "?";
}

/** O avatar do cartão de protagonismo: iniciais do porta-voz mais citado,
 *  ou o que a pessoa escolher (nome, emoji ou foto). Fica NESTA aba, sem
 *  gravar — no v1 era igual, e lá ele vinha de um serviço de fora
 *  (dicebear), que aqui não entra. */
function ProtagonistAvatar({ fallbackName }: { fallbackName: string }) {
  const [avatar, setAvatar] = useState<Avatar | null>(null);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const file = useRef<HTMLInputElement>(null);
  const shown: Avatar = avatar ?? { kind: "initials", text: initials(fallbackName) };
  const apply = () => {
    const value = text.trim();
    if (value) setAvatar(/\p{Extended_Pictographic}/u.test(value) && value.length <= 4 ? { kind: "emoji", text: value } : { kind: "initials", text: initials(value) });
    setEditing(false);
  };
  return (
    <div className="theme-avatar-wrap">
      <button type="button" className="theme-avatar" onClick={() => setEditing(current => !current)} aria-label="Trocar o avatar do porta-voz" aria-expanded={editing}>
        {shown.kind === "image" ? <img src={shown.src} alt=""/> : <span>{shown.text}</span>}
      </button>
      {editing && (
        <div className="theme-avatar-editor" role="dialog" aria-label="Personalizar avatar">
          <label>
            <span>Nome ou emoji</span>
            <input value={text} onChange={event => setText(event.target.value)} placeholder="Ex.: Ana, ou 🙂" onKeyDown={event => { if (event.key === "Enter") apply(); }}/>
          </label>
          <div className="theme-avatar-actions">
            <button type="button" className="secondary-button" onClick={() => file.current?.click()}><ImagePlus size={14} aria-hidden="true"/>Foto</button>
            <button type="button" className="primary-button" onClick={apply}>Aplicar</button>
          </div>
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            onChange={event => {
              const chosen = event.target.files?.[0];
              if (!chosen) return;
              const reader = new FileReader();
              reader.onload = () => { if (typeof reader.result === "string") setAvatar({ kind: "image", src: reader.result }); setEditing(false); };
              reader.readAsDataURL(chosen);
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Os textos "Positivo" e "Negativo", gravados no relatório SALVO por
 *  recorte de temas (decisão de 2026-10-25). Sem texto gravado, abrem no
 *  rascunho montado com os números do recorte. */
function ThemeNotes({ token, reference, facets, data }: {
  token: string;
  reference: ReportReference;
  facets: DashboardFacets;
  data: SocialReportDashboard;
}) {
  const recorte = useMemo(() => themeRecorte(facets), [facets]);
  const key = recorteKey(recorte);
  const draftPositive = draftThemeText(data, "positive");
  const draftNegative = draftThemeText(data, "negative");
  const [notes, setNotes] = useState<ThemeNote[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [positive, setPositive] = useState(draftPositive);
  const [negative, setNegative] = useState(draftNegative);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const saved = reference.kind === "report";
  const current = notes?.find(note => recorteKey(note.recorte) === key) ?? null;

  useEffect(() => {
    if (!saved) { setNotes([]); return; }
    let alive = true;
    setLoadError(null);
    apiClient.listReportThemeNotes(token, reference.id)
      .then(result => { if (alive) setNotes(result); })
      .catch(cause => { if (alive) { setNotes([]); setLoadError(errorMessage(cause, "Não foi possível carregar os textos salvos.")); } });
    return () => { alive = false; };
  }, [token, saved, reference.id]);

  // Troca de recorte (ou chegada dos textos): o gravado vence; sem ele, o
  // rascunho. Texto que a pessoa está editando não é atropelado.
  useEffect(() => {
    if (dirty) return;
    setPositive(current?.positive_text ?? draftPositive);
    setNegative(current?.negative_text ?? draftNegative);
  }, [key, current?.updated_at, draftPositive, draftNegative]);

  useEffect(() => { setDirty(false); setSaveError(null); }, [key]);

  const save = async (positiveText: string, negativeText: string): Promise<boolean> => {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await apiClient.saveReportThemeNote(token, reference.id, { recorte, positive_text: positiveText, negative_text: negativeText });
      setNotes(list => {
        const others = (list ?? []).filter(note => recorteKey(note.recorte) !== key);
        return result ? [result, ...others] : others;
      });
      setDirty(false);
      if (!result) { setPositive(draftPositive); setNegative(draftNegative); }
      return true;
    } catch (cause) {
      setSaveError(errorMessage(cause, "Não foi possível salvar os textos."));
      return false;
    } finally {
      setSaving(false);
    }
  };
  // Sair com texto editado e não salvo pergunta antes. Só no relatório
  // SALVO: o do rascunho não grava, então não há o que salvar.
  useUnsavedChanges(saved && dirty, () => save(positive, negative), "os textos da Análise de Temas");

  const blocks = [
    { tone: "positive" as const, title: "Positivo", value: positive, set: setPositive },
    { tone: "negative" as const, title: "Negativo", value: negative, set: setNegative }
  ];
  return (
    <section className="theme-notes" aria-label="Análise qualitativa">
      {blocks.map(block => (
        <label key={block.tone} className={`theme-note theme-note--${block.tone}`}>
          <span className="theme-note-title">{block.title}</span>
          <textarea
            value={block.value}
            rows={4}
            onChange={event => { block.set(event.target.value); setDirty(true); }}
          />
        </label>
      ))}
      <footer className="theme-notes-foot">
        <span className="theme-notes-meta" aria-live="polite">
          {!saved
            ? "Rascunho de amostra: salve a amostra pra guardar estes textos."
            : current
              ? `Salvo${current.updated_by_name ? ` por ${current.updated_by_name}` : ""} em ${formatDateTime(new Date(current.updated_at))}${dirty ? " · alterações não salvas" : ""}`
              : dirty ? "Alterações não salvas" : "Rascunho sugerido pelos números do recorte — edite e salve."}
          {loadError && <span className="dashboard-inline-error"> {loadError}</span>}
          {saveError && <span className="dashboard-inline-error" role="alert"> {saveError}</span>}
        </span>
        <span className="theme-notes-actions">
          {current && <button type="button" className="secondary-button" disabled={saving} onClick={() => void save("", "")}>Voltar ao rascunho</button>}
          <button type="button" className="primary-button" disabled={!saved || saving || (!dirty && Boolean(current))} onClick={() => void save(positive, negative)}>
            {saving ? "Salvando…" : "Salvar textos"}
          </button>
        </span>
      </footer>
    </section>
  );
}

/**
 * A "Análise de Temas" do v1 (página `theme-deep`): um recorte de grupos,
 * temas e fontes, os números dele e os dois textos de análise. Os filtros
 * aqui são os MESMOS da gaveta do dashboard — mexer num mexe no outro.
 * Embaixo, o detalhe por tema e por grupo que a aba já tinha no v2.
 */
export function ThemesTab({ data, token, reference, facets, controls, children }: {
  data: SocialReportDashboard;
  token: string;
  reference: ReportReference;
  facets: DashboardFacets;
  controls: FacetControls;
  children?: ReactNode;
}) {
  const heading = themeHeading(facets, data);
  const kpis = themeKpis(data);
  const topVoice = (data.spokespersons ?? []).find(item => item.key !== "__none__");
  const filter = (key: "themeGroups" | "themes" | "sources", label: string, options: Array<MultiSelectOption<string>>) => (
    <MultiSelectFilter
      closeOnOutsideClick
      label={label}
      options={options}
      selected={facets[key].values}
      exclude={facets[key].exclude}
      onExcludeChange={value => controls.setExclude(key, value)}
      onToggle={value => controls.toggle(key, value)}
      onClear={() => controls.clear(key)}
    />
  );
  const share = (value: number | null) => (value === null ? "—" : `${value}%`);
  // Relatório de uma fonte só não tem fonte pra escolher (2026-10-30).
  const withSource = offersSourceChoice(data);
  return (
    <div className="dashboard-section-grid">
      <Panel title="Foco no Tema" subtitle="Temas, subtemas e fontes desta análise — os mesmos filtros da gaveta Filtrar" className="dashboard-span-12">
        <div className={`theme-focus-filters${withSource ? "" : " theme-focus-filters--two"}`}>
          {filter("themeGroups", "Tema", controls.options("themeGroups"))}
          {filter("themes", "Subtema", controls.options("themes"))}
          {withSource && filter("sources", "Fonte", sourceOptions)}
        </div>
      </Panel>
      <section className="dashboard-panel dashboard-span-12 theme-deep" aria-label="Análise temática">
        <header className="theme-deep-head">
          <span className="theme-deep-icon" aria-hidden="true"><Target size={18}/></span>
          <div className="theme-deep-heading">
            <h2 className="theme-deep-title">{heading.title}</h2>
            {heading.groups && <p className="theme-deep-groups">{heading.groups}</p>}
            <p className="theme-deep-themes">{heading.themes}</p>
          </div>
          <ChartDownload name="Análise temática"/>
        </header>
        <div className="theme-deep-kpis">
          <article className="theme-kpi">
            <span className="theme-kpi-icon" aria-hidden="true"><TrendingUp size={16}/></span>
            <strong>{formatCompact(kpis.interactions)}</strong>
            <span>Engajamento</span>
            <dl>
              <div><dt>Alcance potencial</dt><dd>{formatCompact(kpis.reach)}</dd></div>
              <div><dt>Publicações</dt><dd>{formatInteger(kpis.mentions)}</dd></div>
            </dl>
          </article>
          <article className="theme-kpi theme-kpi--accent">
            <ProtagonistAvatar fallbackName={topVoice?.key ?? (data.brands[0] ? metricLabel(data.brands[0]) : "Marca")}/>
            <strong>{share(kpis.protagonistShare)}</strong>
            <span>Protagonista</span>
            <p>Menções em que a marca é a protagonista da matéria ou do post.</p>
          </article>
          <article className="theme-kpi theme-kpi--positive">
            <span className="theme-kpi-icon" aria-hidden="true"><Plus size={16}/></span>
            <strong>{share(kpis.positiveShare)}</strong>
            <span>Positivo ou favorável</span>
            <p>{formatInteger(kpis.positive)} menções, sobre as classificadas.</p>
          </article>
          <article className="theme-kpi theme-kpi--negative">
            <span className="theme-kpi-icon" aria-hidden="true"><Minus size={16}/></span>
            <strong>{share(kpis.negativeShare)}</strong>
            <span>Negativo ou desfavorável</span>
            <p>{formatInteger(kpis.negative)} menções, sobre as classificadas.</p>
          </article>
        </div>
        <ThemeNotes token={token} reference={reference} facets={facets} data={data}/>
      </section>
      {children}
      <Panel title="Temas" subtitle="Subtemas consolidados por tema" className="dashboard-span-12" downloadable>
        <RankedBars items={data.theme_groups.map(item => ({ key: item.key, label: metricLabel(item), value: item.count }))} noun="menções" limit={12}/>
      </Panel>
    </div>
  );
}
